/**
 * App Shell layout — sidebar + topbar + content area.
 *
 * On desktop the sidebar is fixed; on mobile it becomes an overlay
 * managed by `mobileSidebarOpen` in the UI store.
 *
 * Cross-References:
 *   - Style Schema §2.2: App Shell
 *   - FR-UI-003: Main content area
 *   - FR-UI-006: Mobile responsive behavior
 *   - Accessibility §5: Skip-to-content link
 */
import { useEffect, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { useUiStore } from '@/stores/uiStore';
import { useMediaQuery, BREAKPOINTS } from '@/hooks/useMediaQuery';
import { useVisitRecorder } from '@/hooks/useVisitRecorder';
import AiChatBubble from '@/modules/ai/components/AiChatBubble';
import { SensitiveAccessBanner } from '@/components/employment/SensitiveAccessBanner';

interface LayoutProps {
  children: ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const { sidebarCollapsed } = useUiStore();
  const isMobile = useMediaQuery(BREAKPOINTS.mobile);
  const { pathname } = useLocation();
  // ACT-1 — record settled page visits for My Last Activity.
  useVisitRecorder();

  // Reset window scroll on route change. Prevents the next page from
  // inheriting the previous page's scroll position (which read as
  // "starts slightly scrolled down" on the AI Chat / dense pages).
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  const shellClasses = [
    'app-shell',
    !isMobile && sidebarCollapsed ? 'app-shell--collapsed' : '',
    isMobile ? 'app-shell--mobile' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={shellClasses}>
      {/* Accessibility: skip-to-content link */}
      <a href="#main-content" className="sr-only sr-only--focusable">
        Skip to content
      </a>

      <Sidebar />
      <div className="app-shell__main">
        <Header />
        <main id="main-content" className="app-shell__content">
          <SensitiveAccessBanner />
          {children}
        </main>
      </div>

      {/* Global floating AI assistant bubble. Auto-hides on /modules/ai/chat. */}
      <AiChatBubble />
    </div>
  );
}
